/**
 * @file: hooks.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { usePresentationMode } from '@euler/app/components/PresentationModeContext';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { useAppLoading } from '@euler/components/loading/Provider';
import { makeDebug, showAlert } from '@euler/utils';

import { kIdentity } from '@euler/functions/constants';
import { clientPlatformRefused$ } from '@euler/functions/entitlements/clientPlatformRefused';
import { HttpError } from '@euler/lib/request';
import { getSessionId, pruneSession } from '@euler/lib/session';
import {
  AuthProvider,
  AuthProviderType,
  AuthenticatorNextActionApp,
  AuthenticatorResult,
} from '@euler/model/auth';
import { IdType } from '@euler/model/common';
import { useServiceFactory } from '@euler/services/factory';
import { AuthThemeProps } from '@euler/typings/styled';
import { useCallback, useEffect, useState } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import { useTheme } from 'styled-components';
import { useAuthContext, useCurrentUser } from './context';
import { useHandleLoginResult } from './useHandleLoginResult';

const debug = makeDebug('auth:context');

type LoginFunction = <T extends AuthProvider>(
  authProviderData: T,
) => Promise<AuthenticatorResult<T> | undefined>;

export const useAuthTheme = (): AuthThemeProps => {
  const presentationMode = usePresentationMode();
  const presentationTheme = usePresentationTheme();
  const normalTheme = useTheme();
  return presentationMode === 'presentation'
    ? presentationTheme.auth
    : normalTheme.auth;
};

export const useLogin = (props?: {
  onError?: (e: Error) => void;
  onAppSelection?: (
    apps: AuthenticatorNextActionApp[],
    authToken: string,
  ) => void;
}) => {
  const { onError, onAppSelection } = props ?? {};
  const authContext = useAuthContext();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const { authService } = useServiceFactory();

  const internalHandleLoginResult = useHandleLoginResult(
    authContext.authState$,
  );

  const handleLoginResult = useCallback(
    async (
      providerType: AuthProviderType,
      result: Awaited<ReturnType<typeof authService.login>>,
    ) => {
      await internalHandleLoginResult(providerType, result);
    },
    [authService, internalHandleLoginResult],
  );

  const login: LoginFunction = useCallback(
    async data => {
      try {
        setIsLoggingIn(true);

        const result = await authService.login(data);

        if (result.nextAction?.type === 'federation-continuation') {
          return result;
        }

        if (result.nextAction?.type === 'app-selection') {
          onAppSelection?.(result.nextAction.apps, result.token);
          return result;
        }

        await handleLoginResult(data.type, result);

        userLoggedOut$.next(false);

        return result;
      } catch (e) {
        const err = e as HttpError;
        if (onError) {
          onError(e as Error);
        } else {
          const { code, subCode } = err;
          if (code === 'auth_error') {
            let errorMsg = '';
            switch (subCode) {
              case 'user_not_found':
                errorMsg = '用户不存在!';
                break;
              case 'auth_user_store_not_bound':
              case 'auth_org_not_bound':
              case 'auth_store_not_bound':
                errorMsg = '对不起，该用户类型不支持在APP端登录！';
                break;
              case 'store_service_finished':
                errorMsg = '登录失败 (Service Ended)！';
                break;
              case 'password_auth_invalid_mobile':
                errorMsg = '手机号格式有误！';
                break;
              case 'password_auth_invalid_password':
                errorMsg = '登录密码错误！';
                break;
              case 'invalid_verify_code':
                errorMsg = '验证码错误！';
                break;
              case 'qrcode_auth_expired':
                errorMsg = '二维码已失效！';
                break;
              default:
                errorMsg = '对不起，登录验证失败，请稍候重试！';
                break;
            }
            void showAlert(errorMsg);
          } else {
            void showAlert('对不起，登录失败，服务暂时不可用，请稍后重试！');
          }
        }
      } finally {
        setIsLoggingIn(false);
      }
    },
    [authService, handleLoginResult, onAppSelection, onError],
  );
  return {
    isLoggingIn,
    login,
    handleLoginResult,
  };
};

export const userLoggedOut$ = new BehaviorSubject<boolean>(false);

export const useLogout = () => {
  const context = useAuthContext();
  const { userService, defaultTokenService, defaultStorageService } =
    useServiceFactory();
  const loading = useAppLoading();
  return useCallback(async () => {
    const { sessionId } = await getSessionId();
    if (!context.authState$.value.isAuthenticated) return;
    try {
      debug(
        'user presence logged off session: %s, uid: %s',
        sessionId,
        context.authState$.value.authenticatedUserInfo.user.uid,
      );
      userLoggedOut$.next(true);
      clientPlatformRefused$.next(false);
      loading.show();
      await userService.presenceLogout(
        context.authState$.value.authenticatedUserInfo.user.uid,
      );
    } catch (e) {
      console.error(e);
    }
    try {
      await pruneSession();
      await defaultTokenService.removeToken();
      await defaultTokenService.removeRefreshToken().catch(() => {});
      await defaultTokenService.removeRefreshTokenExpiry().catch(() => {});
      await defaultStorageService.remove(kIdentity);
    } catch (e) {
      console.error(e);
    }
    userAccountSwitched$.next('');
    context.authState$.next({ isAuthenticated: false });
    loading.hide();
  }, [
    context.authState$,
    defaultStorageService,
    defaultTokenService,
    loading,
    userService,
  ]);
};

// the org user's uid not the platform user's uid
export const userAccountSwitched$ = new Subject<string>();

export const useAuthContextKey = () => {
  const [authContextKey, setAuthContextKey] = useState('');
  useEffect(() => {
    const sub = userAccountSwitched$.subscribe(uid => {
      setAuthContextKey(uid);
    });
    return () => sub.unsubscribe();
  }, []);
  return authContextKey;
};

export const useSwitchAccount = () => {
  const { defaultTokenService, authService } = useServiceFactory();
  const authUser = useCurrentUser();
  const context = useAuthContext();
  const handleLoginResult = useHandleLoginResult(context.authState$);
  return useCallback(
    async (appId: IdType) => {
      if (authUser == null) return;
      const res = await authService.switchApp(appId);
      await defaultTokenService.removeToken();
      await handleLoginResult(AuthProviderType.TokenExchange, res);

      userAccountSwitched$.next(res.results.user.uid);
    },
    [authService, authUser, defaultTokenService, handleLoginResult],
  );
};

export const useAuthenticatedUser = () => {
  const context = useAuthContext();
  if (!context) {
    throw new Error(
      'useAuthenticatedUser is expected to be called for a valid AuthContext only',
    );
  }
  if (!context.authState$.value.isAuthenticated) {
    throw new Error(
      'Try to get the currently authenticated user in an unauthenticated user context',
    );
  }
  return context.authState$.value.authenticatedUserInfo;
};
